import { ChangeDetectionStrategy, Component, computed, contentChild, input, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { schemaErrorOf, SchemaLoadState, schemaOf } from '../loader/schema-loader.types';
import { SchemaNode } from '../model/schema-node.types';
import { SchemaNodeOutletComponent } from './schema-node-outlet.component';

/**
 * What a consumer renders for a schema that is still being fetched. It owns the branching and
 * nothing else — the loading and error markup arrives as projected templates, because
 * `kros-core/schema` must keep its single workspace dependency and any built-in spinner would drag a
 * UI library in.
 *
 * **A missing `#loading` or `#error` template renders nothing.** That is deliberate: it lets a
 * consumer move onto this outlet without changing what the user sees, and giving the state a message
 * afterwards is a template and no other change.
 *
 * This is the schema-load tier. The `[state]` input of {@link SchemaNodeOutletComponent} is the data
 * tier, and the two never merge: a schema that failed to arrive brings no `states` slots with it, so
 * its own load failure can never be resolved declaratively.
 */
@Component({
  selector: 'kros-schema-root-outlet',
  imports: [NgTemplateOutlet, SchemaNodeOutletComponent],
  template: `
    @if (errorMessage(); as message) {
      @if (errorTemplate(); as errorTpl) {
        <ng-container *ngTemplateOutlet="errorTpl; context: { $implicit: message }" />
      }
    } @else if (schema(); as loadedSchema) {
      @if (loadedTemplate(); as loadedTpl) {
        <ng-container *ngTemplateOutlet="loadedTpl; context: { $implicit: loadedSchema }" />
      } @else {
        <kros-schema-node-outlet [node]="loadedSchema" />
      }
    } @else if (loadingTemplate(); as loadingTpl) {
      <ng-container *ngTemplateOutlet="loadingTpl" />
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchemaRootOutletComponent<TSchema extends SchemaNode> {
  readonly state = input.required<SchemaLoadState<TSchema>>();

  /** Receives the loaded schema as `$implicit`. Absent ⇒ the schema goes to a plain node outlet. */
  readonly loadedTemplate = contentChild('loaded', { read: TemplateRef });
  readonly loadingTemplate = contentChild('loading', { read: TemplateRef });
  /** Receives the error message as `$implicit`. */
  readonly errorTemplate = contentChild('error', { read: TemplateRef });

  /** Present while `loaded` **and** `reloading`, so a refetch keeps rendering the previous schema. */
  protected readonly schema = computed(() => schemaOf(this.state()));

  protected readonly errorMessage = computed(() => schemaErrorOf(this.state()));
}
